import { Inject, Injectable } from '@angular/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import ZoomPlugin from './zoom.plugin';
import Env from '../../env';
import { NgRedux } from '@angular-redux/store';

@Injectable({
    providedIn: 'root'
})
export class ZoomService {
    private readonly MAX_SCALE_FACTOR = '2.8';
    private readonly MIN_SCALE_FACTOR = '0.6';

    constructor(
        @Inject('ENV') private env: Env,
        private ngRedux: NgRedux<any>
    ) {
        if (Capacitor.isNativePlatform() && this.env.ALLOW_DYNAMIC_CONTENT_SCALING) {
            this.initializeTextZoom().then(() => {
                console.log('Zoom service started');
            });
        }
    }

    /**
     * Default is '1' for 'Normal', but the user can increase/decrease the value through OS settings
     * Example values are 0.8 Smaller, 1 Normal, 1.2 Larger etc.
     * iOS and Android may emit different values.
     *
     * --scale-factor scales rem units, affecting all text on the page under class .scaled
     * --max-scale-factor caps the maximum scale factor
     * See _dynamic-font-size.scss for more details.
     *
     * App should prioritize important content what to scale more or less when responding to text-size changes.
     * Not all content can be scaled equally there is not enough space
     * See design: https://www.figma.com/design/WfIs9AmCGhloc1sPMPCyDa/Project-App-GENERIC?node-id=7079-173125&t=oBfz8I5bkAQXNGEf-0
     * See tips: https://developer.apple.com/design/human-interface-guidelines/typography#Conveying-hierarchy
     *
     */
    private async initializeTextZoom() {
        // Fetch the initial scale factor on startup
        this.setTextZoom(await this.getScaleFactor());

        // Listen for text zoom changes after the app is resumed
        if (Capacitor.getPlatform() === 'ios') {
            CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
                if (isActive) {
                    this.setTextZoom(await this.getScaleFactor());
                }
            });
        }

        // Listen for text zoom changes after the app is resumed
        if (Capacitor.getPlatform() === 'android') {
            window.addEventListener('appFocusChanged', async (event: unknown) => {
                if (event && typeof event === 'object' && 'hasFocus' in event) {
                    if (event?.['hasFocus']) {
                        this.setTextZoom(await this.getScaleFactor());
                    }
                }
            });
        }
    }

    private async getScaleFactor(): Promise<number> {
        try {
            const { scaleFactor: fetchedScale } = await ZoomPlugin.getInitialScale();
            return fetchedScale ?? 1.0;
        } catch (error) {
            console.error('Error fetching text zoom scale factor:', error);
            return 1.0; // Default scale factor in case of an error
        }
    }

    private setTextZoom(scaleFactor: number) {
        // Round to one decimal place
        scaleFactor = Math.round(scaleFactor * 10) / 10;

        // Limit accessibility scaling levels so it doesn't go ridiculously out of scale
        if (scaleFactor < 0.6) {
            scaleFactor = 0.6;
        } else if (scaleFactor > 1.0 && scaleFactor <= 1.2) {
            scaleFactor = 1.0;
        } else if (scaleFactor > 1.8 && scaleFactor < 2.0) {
            scaleFactor = 1.85;
        } else if (scaleFactor >= 2.0 && scaleFactor < 2.2) {
            scaleFactor = 1.9;
        } else if (scaleFactor >= 2.2 && scaleFactor < 2.4) {
            scaleFactor = 1.95;
        } else if (scaleFactor >= 2.4 && scaleFactor < 2.6) {
            scaleFactor = 2.0;
        } else if (scaleFactor >= 2.6) {
            scaleFactor = 2.05;
        }

        // The scale to which elements are scaled
        document.documentElement.style.setProperty('--scale-factor', `${scaleFactor}`);
        // The maximum and minimum scale factors over which elements can't be scaled
        document.documentElement.style.setProperty('--max-scale-factor', this.MAX_SCALE_FACTOR);
        document.documentElement.style.setProperty('--min-scale-factor', this.MIN_SCALE_FACTOR);

        const htmlElement = document.documentElement;
        const isScaled = htmlElement.classList.contains('scaled');

        // Must be added for scaling variables to take effect
        if (scaleFactor !== 1 && !isScaled) {
            htmlElement.classList.add('scaled');
        } else if (scaleFactor === 1 && isScaled) {
            htmlElement.classList.remove('scaled');
        }
        this.observeAndUpdateTermsIframes();
    }

    /**
     * Observes the DOM for new iframes and updates their styles
     * Since their iframed CORS policy, it can not directly inherit the parent's styles
     * The iframed content must have script which listens to dynamic updates
     */
    private observeAndUpdateTermsIframes(): void {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement) {
                        const iframes = node.querySelectorAll?.('iframe') || [];
                        iframes.forEach((iframe) => {
                            if (iframe?.classList.contains('tnc__iframe')) {
                                this.waitForIframeLoad(iframe);
                            }
                        });
                    }
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });
    }

    private waitForIframeLoad(iframe: HTMLIFrameElement): void {
        if (iframe.contentWindow && iframe.contentDocument?.readyState === 'complete') {
            this.sendStylesToIframe(iframe);
        } else {
            iframe.addEventListener('load', () => {
                this.sendStylesToIframe(iframe);
            });
        }
    }

    sendStylesToIframe(iframe) {
        const scaleFactor = document.documentElement.style.getPropertyValue('--scale-factor') || '1';
        const maxScaleFactor = 1.5;
        const minScaleFactor = 0.6;

        const message = {
            type: 'style-update',
            styles: `
            :root {
                --scale-factor: ${scaleFactor};
                --max-scale-factor: ${maxScaleFactor};
                --min-scale-factor: ${minScaleFactor};
            }

            .scaled {
                body {
                    overflow-wrap: break-word;
                    font-size: calc(1rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor)));
                    line-height: calc(1rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor)));
                }

                h1, h2, h3, h4, h5, h6 {
                    font-size: calc(1.5rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor)));
                    line-height: calc(1.5rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor)));
                }
            }
        `
        };

        const terms = this.ngRedux.getState().core.region.terms;
        const termsUrl = `${terms?.content_base_url}`;

        iframe.addEventListener('load', () => {
            iframe.contentWindow.postMessage(message, termsUrl ?? '*');
        });

        if (iframe.contentWindow && iframe.contentDocument?.readyState === 'complete') {
            iframe.contentWindow.postMessage(message, termsUrl ?? '*');
        }
    }
}
